import axios from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface ImageBanner {
  id: number;
  image: string;
  title: string;
  description?: string;
  link?: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface ImageBannerRequest {
  image?: string;
  title: string;
  description?: string;
  link?: string;
  is_visible?: boolean;
  sort_order?: number;
}

export interface ImageBannerResponse {
  success: boolean;
  data: ImageBanner[];
  message?: string;
}

export interface SingleImageBannerResponse {
  success: boolean;
  data: ImageBanner;
  message?: string;
}

class ImageBannerService {
  // Use real API with database
  private baseURL = `${API_BASE_URL}/image-banners`;

  // Get all image banners (admin view)
  async getAllImageBanners(includeHidden: boolean = false): Promise<ImageBanner[]> {
    try {
      const url = includeHidden ? `${this.baseURL}?includeHidden=true` : this.baseURL;
      const response = await axios.get<ImageBannerResponse>(url);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch image banners');
      }
    } catch (error) {
      console.error('Error fetching image banners:', error);
      throw error;
    }
  }

  // Get only visible image banners for public display
  async getVisibleImageBanners(): Promise<ImageBanner[]> {
    try {
      const response = await axios.get<ImageBannerResponse>(`${this.baseURL}/visible`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch visible image banners');
      }
    } catch (error) {
      console.error('Error fetching visible image banners:', error);
      throw error;
    }
  }


  // Get single image banner by ID
  async getImageBannerById(id: number): Promise<ImageBanner> {
    try {
      const response = await axios.get<SingleImageBannerResponse>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch image banner');
      }
    } catch (error) {
      console.error('Error fetching image banner by ID:', error);
      throw error;
    }
  }

  // Create new image banner with file upload
  async createImageBanner(formData: FormData): Promise<ImageBanner> {
    try {
      const response = await axios.post<SingleImageBannerResponse>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create image banner');
      }
    } catch (error) {
      console.error('Error creating image banner:', error);
      throw error;
    }
  }

  // Update image banner with optional file upload
  async updateImageBanner(id: number, formData: FormData): Promise<ImageBanner> {
    try {
      const response = await axios.put<SingleImageBannerResponse>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update image banner');
      }
    } catch (error) {
      console.error('Error updating image banner:', error);
      throw error;
    }
  }

  // Delete image banner
  async deleteImageBanner(id: number): Promise<boolean> {
    try {
      const response = await axios.delete<{ success: boolean; message?: string }>(`${this.baseURL}/${id}`);
      
      return response.data.success;
    } catch (error) {
      console.error('Error deleting image banner:', error);
      throw error;
    }
  }

  // Toggle visibility
  async toggleVisibility(id: number): Promise<ImageBanner> {
    try {
      const response = await axios.patch<SingleImageBannerResponse>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to toggle visibility');
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // Update sort order for multiple banners
  async updateSortOrder(banners: { id: number; sort_order: number }[]): Promise<boolean> {
    try {
      const response = await axios.put<{ success: boolean; message?: string }>(`${this.baseURL}/sort-order`, {
        banners
      });
      
      return response.data.success;
    } catch (error) {
      console.error('Error updating sort order:', error);
      throw error;
    }
  }

  // Helper method to get image URL
  getImageUrl(imagePath: string | undefined | null): string {
    // Handle undefined, null, or empty string
    if (!imagePath || typeof imagePath !== 'string') {
      return '/assets/images/banner-image.jpg'; // Fallback image
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // If it's a relative path starting with /uploads or /assets, return as is (proxy will handle it)
    if (imagePath.startsWith('/uploads') || imagePath.startsWith('/assets')) {
      return imagePath;
    }
    
    // Otherwise assume it's just a filename, prepend /uploads/
    return `/uploads/${imagePath}`;
  }
}

export default new ImageBannerService();
